import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { IS_NAV_COLLAPSED } from 'src/app/core/constants/local-storage-keys';
import { EXTERNAL_NAV_URLS } from 'src/app/core/constants/mapping-keys';
import { EntityType } from 'src/app/core/models/entitlements/entity';
import { Patient } from 'src/app/core/models/patient';
import { AuthService } from 'src/app/core/services/auth.service';
import { MemberInformationService } from 'src/app/core/services/member-information.service';
import { anyUrls, ExternalNavItem, SecondaryNavItem } from 'src/app/core/utils/routing-util';
import { EnvironmentService } from './environment.service';
import { OrganizationService } from './organization.service';

@Injectable({
  providedIn: 'root'
})
export class SideNavService {
  patient: Patient;

  private _externalNavItems: BehaviorSubject<ExternalNavItem[]> = new BehaviorSubject([]);
  externalNavItems: Observable<ExternalNavItem[]> = this._externalNavItems.asObservable();

  private _secondaryNav: BehaviorSubject<SecondaryNavItem[]> = new BehaviorSubject([]);
  secondaryNav: Observable<SecondaryNavItem[]> = this._secondaryNav.asObservable();

  private get isNavCollapsed(): boolean {
    return JSON.parse(localStorage.getItem(this.isNavCollapsedStorageKey) || 'false');
  }

  private set isNavCollapsed(isNavCollapsed: boolean) {
    if (!this.isSecondaryNavPresent) {
      localStorage.setItem(this.isNavCollapsedStorageKey, JSON.stringify(isNavCollapsed));
    }

    this._navCollapsed.next(isNavCollapsed);

    // Dispatching a window resize event to force ngx-datatable to recalculate table size/column widths.
    // This will not work without the setTimeout() wrapper.
    setTimeout(() => window.dispatchEvent(new Event('resize')));
  }

  private _navCollapsed: BehaviorSubject<boolean> = new BehaviorSubject(this.isNavCollapsed);
  navCollapsed: Observable<boolean> = this._navCollapsed.asObservable();

  private get isSecondaryNavPresent(): boolean {
    return this._secondaryNav.value?.length > 0;
  }

  private get isNavCollapsedStorageKey(): string {
    return `${IS_NAV_COLLAPSED}${this.environmentService.apiBaseUrl}`;
  }

  constructor(
    memberInformationService: MemberInformationService,
    router: Router,
    public authService: AuthService,
    private organizationService: OrganizationService,
    private environmentService: EnvironmentService
  ) {
    const memberIdObservable: Observable<string> = memberInformationService.memberId;
    const navigationEndObservable: Observable<NavigationEnd> = router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event as NavigationEnd)
    );

    combineLatest([memberIdObservable, navigationEndObservable]).subscribe(
      ([memberId, navigationEndEvent]: [string, NavigationEnd]) => {
        if (
          memberId &&
          navigationEndEvent.url.startsWith(`/private/patients/${memberId}`) &&
          this.authService.entity.entityType !== EntityType.PATIENT
        ) {
          this.usePatientSideNav(memberId);

          this.setNavCollapsed(false);
        } else {
          this.usePrimaryNav();

          this.setNavCollapsed(this.isNavCollapsed);
        }
      }
    );
  }

  toggleNavCollapsed() {
    this.isNavCollapsed = !this._navCollapsed.value;
  }

  setNavCollapsed(isNavCollapsed: boolean) {
    this.isNavCollapsed = isNavCollapsed;
  }

  usePrimaryNav() {
    this._secondaryNav.next([]);

    if (this.authService.isAuthenticated()) {
      const externalNavUrlsMappingKey = this.organizationService.getCachedMappingKey(EXTERNAL_NAV_URLS);

      if (externalNavUrlsMappingKey) {
        const externalNavItems = JSON.parse(externalNavUrlsMappingKey) as ExternalNavItem[];

        this._externalNavItems.next(
          (externalNavItems || []).filter((navItem) => this.authService.isEntitled(navItem.entitlementUrl as string))
        );
      }
    }
  }

  usePatientSideNav(memberId: string) {
    const secondaryNav: SecondaryNavItem[] = [
      {
        routerLink: `/private/patients/${memberId}/details`,
        displayText: 'Member Home', // NOTE: SideNavComponent#updateHeaderText will also have to be changed if you change this text
        entitlementUrl: '/patients/viewPatientDetail.html'
      },
      {
        routerLink: `/private/patients/${memberId}/baseball-card`,
        displayText: 'MHN Baseball Card',
        entitlementUrl: '/baseballcard/getBaseballCard.html'
      },
      {
        routerLink: `/private/patients/${memberId}/allergies`,
        displayText: 'Allergies',
        entitlementUrl: '/patients/getAllergies.html'
      },
      {
        routerLink: `/private/patients/${memberId}/all-action-plans`,
        displayText: 'Action Plans',
        entitlementUrl: anyUrls(
          '/sdohactionplan/fetchSDOHActionPlans.html',
          '/maternityactionplan/fetchMaternityActionPlans.html',
          '/wellnessrecoveryactionplan/fetchWellnessRecoveryActionPlans.html'
        )
      },
      {
        routerLink: `/private/patients/${memberId}/assessments`,
        displayText: 'Assessments',
        entitlementUrl: '/assessment/displaymemberassessments.html'
      },
      {
        routerLink: `/private/patients/${memberId}/authorizations`,
        displayText: 'Authorizations',
        entitlementUrl: '/authorization/retrieveMemberAuthorizationRecords.html'
      },
      {
        routerLink: `/private/patients/${memberId}/care-gaps`,
        displayText: 'Care Gaps',
        entitlementUrl: '/patients/careReminderSchedule.html'
      },
      {
        routerLink: `/private/patients/${memberId}/care-plans`,
        displayText: 'Care Plans',
        entitlementUrl: '/careplan/displayMaintaincareplan.html'
      },
      {
        routerLink: `/private/patients/${memberId}/build-care-team`,
        displayText: 'Care Team',
        entitlementUrl: '/patients/buildCareTeam.html'
      },
      {
        routerLink: `/private/patients/${memberId}/case-review`,
        displayText: 'Case Review',
        entitlementUrl: anyUrls(
          '/case-review/redirect.html',
          '/depression-case-review/fetchAllDepressionCaseReview.html'
        )
      },
      {
        routerLink: `/private/patients/${memberId}/action-plans`,
        displayText: 'CC Action Plans',
        entitlementUrl: '/actionplan/fetchFilledQuestionnaireActionPlans.html'
      },
      {
        routerLink: `/private/patients/${memberId}/chronic-care-management-status`,
        displayText: 'Chronic Care Management Status',
        entitlementUrl: '/memberfacet/ccm/getAddedMemberFacetStatuses.html'
      },
      {
        routerLink: `/private/patients/${memberId}/claims`,
        displayText: 'Claims',
        entitlementUrl: '/claims/getClaims.html'
      },
      {
        routerLink: `/private/patients/${memberId}/contact-logs-search`,
        displayText: 'Contact Log/Progress Note',
        entitlementUrl: '/contactInfo/getPatientContactHistory.html'
      },
      {
        routerLink: `/private/patients/${memberId}/documents`,
        displayText: 'Documents',
        entitlementUrl: '/patientdoc/displayDocumentManager.html'
      },
      {
        routerLink: `/private/patients/${memberId}/education-material`,
        displayText: 'Education',
        entitlementUrl: '/education/displayassigneducation.html'
      },
      {
        routerLink: `/private/patients/${memberId}/eligibility`,
        displayText: 'Eligibility',
        entitlementUrl: '/patients/getPatientEligibilityHistory.html'
      },
      {
        routerLink: `/private/patients/${memberId}/hospital-activity`,
        displayText: 'Hospital Activity',
        entitlementUrl: '/patientadmission/retrieveMemberAdmissionRecords.html'
      },
      {
        routerLink: `/private/patients/${memberId}/med-rec`,
        displayText: 'Medication Reconciliation',
        entitlementUrl: '/bundle/fetchMedicationReconciliations.html'
      },
      {
        routerLink: `/private/patients/${memberId}/rx-history`,
        displayText: 'Medication Fills',
        entitlementUrl: '/patientmed/rxHistory.html'
      },
      {
        routerLink: `/private/patients/${memberId}/member-status`,
        displayText: 'Member Engagement Status',
        entitlementUrl: '/memberstatus/getAddedMemberStatuses.html'
      },
      {
        routerLink: `/private/patients/${memberId}/messages`,
        displayText: 'Messages',
        entitlementUrl: '/patients/fetchPatientMessages.html'
      },
      {
        routerLink: `/private/patients/${memberId}/programs`,
        displayText: 'Programs',
        entitlementUrl: '/patients/viewPatientCondition.html'
      },
      {
        routerLink: `/private/patients/${memberId}/referrals`,
        displayText: 'Referrals',
        entitlementUrl: '/referral/view_memberReferral.html'
      },
      {
        routerLink: `/private/patients/${memberId}/risk`,
        displayText: 'Risk',
        entitlementUrl: '/patients/fetchRiskHistory.html'
      },
      {
        routerLink: `/private/patients/${memberId}/worklist`,
        displayText: 'Tasks',
        entitlementUrl: '/careplan/getAllAssignedCareplaninterventions.html'
      },
      {
        routerLink: `/private/patients/${memberId}/timeline`,
        displayText: 'Timeline',
        entitlementUrl: '/members/timeline.html'
      },
      {
        routerLink: `/private/patients/${memberId}/toc-bundle`,
        displayText: 'TOC Bundle',
        entitlementUrl: '/bundle/fetchTOCBundleTasks.html'
      },
      {
        routerLink: `/private/patients/${memberId}/vitals`,
        displayText: 'Vitals',
        entitlementUrl: '/patients/getPatientBiometricServices.html'
      },
      {
        routerLink: `/private/patients/${memberId}/vitals-charts`,
        displayText: 'Vitals Charts',
        entitlementUrl: '/patients/getBiometricsServiceData.html'
      },
      {
        routerLink: `/private/patients/${memberId}/waivers`,
        displayText: 'Waivers',
        entitlementUrl: '/patients/getPatientWaivers.html'
      }
    ];

    this._secondaryNav.next(secondaryNav);
  }
}
